import messages from '../locale/jsonMessages';
import { isOptionBookable, OPTION_LIMITS } from './bookingOptionsBilling';

// Presentation helpers shared by the owner, renter, admin and receipt screens.
// Fixed options (delivery, chauffeur) have no stored label: it is translated from their code.
const FIXED_PRESENTATION = {
  delivery: { label: messages.boDeliveryLabel, description: messages.boDeliveryDescription },
  chauffeur: { label: messages.boChauffeurLabel, description: messages.boChauffeurDescription },
};

const fixedCode = (option) => {
  if (!option) return null;
  const code = option.labelKey || option.code;
  return (option.isFixed || option.labelKey) && FIXED_PRESENTATION[code] ? code : null;
};

export const getOptionLabel = (option, formatMessage) => {
  const code = fixedCode(option);
  return code ? formatMessage(FIXED_PRESENTATION[code].label) : (option && option.label) || '';
};

export const getOptionDescription = (option, formatMessage) => {
  const code = fixedCode(option);
  return code ? formatMessage(FIXED_PRESENTATION[code].description) : (option && option.description) || '';
};

export const getBillingModeMessage = (billingMode, { short = false } = {}) => {
  if (billingMode === 'per_day') return short ? messages.boPerDayShort : messages.boBillingPerDay;
  return short ? messages.boFlatShort : messages.boBillingFlat;
};

const ERROR_MESSAGES = {
  invalidOptionLabel: messages.boErrLabel,
  reservedOptionLabel: messages.boErrLabelTaken,
  optionLabelTaken: messages.boErrLabelTaken,
  invalidOptionDescription: messages.boErrDescription,
  invalidOptionPrice: messages.boErrPrice,
  invalidBillingMode: messages.boErrBillingMode,
  maxOptionsReached: messages.boErrLimit,
  optionDisabledByAdmin: messages.boErrDisabledByAdmin,
};

// Maps an error code of the shared validation to a translated message descriptor.
export const getOptionErrorMessage = (code) => ERROR_MESSAGES[code] || messages.somethingWentWrong;

// ---------------------------------------------------------------------------
// Renter side: selection, lines of a booking, legacy fallback.
// ---------------------------------------------------------------------------

// Fixed display order: delivery, chauffeur, then the custom options.
export const sortOptions = (options) => [...(options || [])]
  .sort((a, b) => (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0) || Number(a.id) - Number(b.id));

// Options the renter can book (same rule as the server).
export const getBookableOptions = (options) => sortOptions((options || []).filter((option) => isOptionBookable(option)));

export const isDeliveryLocationValid = (value) => {
  const length = typeof value === 'string' ? value.trim().length : 0;
  return length >= OPTION_LIMITS.deliveryLocationMin && length <= OPTION_LIMITS.deliveryLocationMax;
};

// selections: [{ optionId, userInput }] -> { items: priced-ready option lines, dropped: ids no longer bookable }.
export const resolveSelection = (listingOptions, selections) => {
  const items = [];
  const dropped = [];
  const seen = new Set();
  (selections || []).forEach((selection) => {
    const id = Number(selection && selection.optionId);
    if (seen.has(id)) return;
    seen.add(id);
    const option = (listingOptions || []).find((item) => Number(item.id) === id);
    if (!option || !isOptionBookable(option)) {
      dropped.push(id);
      return;
    }
    items.push({ ...option, listingOptionId: option.id, userInput: option.code === 'delivery' ? (selection.userInput || null) : null });
  });
  return { items: sortOptions(items), dropped };
};

// Payload of createReservation / getBillingCalculation: ids and the delivery location only, never prices.
export const toOptionInputs = (items) => (items || []).map((item) => ({
  optionId: Number(item.id),
  userInput: item.code === 'delivery' ? ((item.userInput || '').trim() || null) : null,
}));

// "Baby seat (3 days)" / "Delivery (flat fee)".
export const getOptionLineLabel = (line, formatMessage) => {
  const label = getOptionLabel(line, formatMessage);
  if (line && line.billingMode === 'per_day') {
    return formatMessage(messages.boLineDays, { label, count: Number(line.quantity) || 0 });
  }
  return formatMessage(messages.boLineFlat, { label });
};

// Lines of a stored reservation. Reservations created before the options existed have no snapshot:
// a positive `delivery` becomes a legacy flat "Delivery" line (never both, so nothing is counted twice).
export const getReservationOptionLines = (reservation) => {
  if (!reservation) return [];
  const stored = reservation.reservationOptions;
  if (Array.isArray(stored) && stored.length > 0) return stored;
  const delivery = Number(reservation.delivery);
  if (delivery > 0) {
    return [{
      id: null, code: 'delivery', isFixed: true, label: null, labelKey: 'delivery', description: null,
      billingMode: 'flat', unitPrice: delivery, quantity: 1, amount: delivery, userInput: null, legacy: true,
    }];
  }
  return [];
};

export const sumOptionLines = (lines) => (lines || []).reduce((sum, line) => sum + (Number(line.amount) || 0), 0);

// The payment form keeps the selection as a JSON string ([{ optionId, userInput }]).
export const parseOptionsPayload = (payload) => {
  try {
    const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload;
    return Array.isArray(parsed) ? parsed.filter((item) => item && Number(item.optionId) > 0) : [];
  } catch (error) {
    return [];
  }
};
